import { InterestRegisterEntryType } from './assists-interest-register.constants';

export type InterestRegisterDetailFieldType = 'text' | 'date' | 'boolean' | 'currency' | 'number' | 'decimal';

export interface InterestRegisterDetailFieldConfig {
    key: string;
    fallbackKeys?: string[]; // Previous/legacy formData keys supported so older entries remain displayable after field renames
    labelKey: string;
    type?: InterestRegisterDetailFieldType;
    valueMap?: Record<string, string>;
    showWhenEmpty?: boolean;
}

export interface InterestRegisterDetailSectionConfig {
    titleKey: string;
    fields: InterestRegisterDetailFieldConfig[];
}

export interface InterestRegisterInfoTextConfig {
    summaryTextKey: string;
    detailTextKeys?: string[];
    linkIntroTextKey?: string;
    linkTextKey?: string;
    linkUrl?: string;
}

export const INTEREST_REGISTER_DETAIL_SECTIONS: Record<InterestRegisterEntryType, InterestRegisterDetailSectionConfig[]> = {
    [InterestRegisterEntryType.SecuritiesTrading]: [
        {
            titleKey: 'label_interest_register_section_securities_trading',
            fields: [
                {
                    key: 'transactionDate',
                    fallbackKeys: ['tradeDate', 'date'],
                    labelKey: 'label_interest_register_field_trade_date',
                    type: 'date',
                },
                {
                    key: 'transactionType',
                    fallbackKeys: ['tradeType', 'buySell'],
                    labelKey: 'label_interest_register_field_trade_type',
                    valueMap: {
                        Buy: 'Kaup',
                        buy: 'Kaup',
                        Kaup: 'Kaup',
                        Sell: 'Sala',
                        sell: 'Sala',
                        Sala: 'Sala',
                    },
                },
                {
                    key: 'transactionOwnerName',
                    fallbackKeys: ['ownerName', 'traderName', 'tradingOwnerName'],
                    labelKey: 'label_interest_register_field_trading_owner_name',
                },
                {
                    key: 'financialInstrumentName',
                    fallbackKeys: ['instrumentName', 'instrument', 'financialInstrument', 'instrumentIdentifier'],
                    labelKey: 'label_interest_register_field_instrument_name',
                },
                {
                    key: 'isPublicOffering',
                    fallbackKeys: ['publicOffering'],
                    labelKey: 'label_interest_register_field_is_public_offering',
                    type: 'boolean',
                },
                {
                    key: 'nominalValue',
                    fallbackKeys: ['nominalAmount'],
                    labelKey: 'label_interest_register_field_nominal_value',
                    type: 'number',
                },
                {
                    key: 'transactionPrice',
                    fallbackKeys: ['price', 'tradePrice'],
                    labelKey: 'label_interest_register_field_price',
                    type: 'currency',
                },
                {
                    key: 'otherInformation',
                    fallbackKeys: ['lateNotificationReason', 'lateReason', 'delayedNotificationReason', 'lateNotificationReason'],
                    labelKey: 'label_interest_register_field_other_information',
                },
            ],
        },
    ],
    [InterestRegisterEntryType.ConflictOfInterest]: [
        {
            titleKey: 'label_interest_register_section_conflict',
            fields: [
                {
                    key: 'area',
                    labelKey: 'label_interest_register_field_area',
                },
                {
                    key: 'projectType',
                    labelKey: 'label_interest_register_field_project_type',
                },
                {
                    key: 'description',
                    labelKey: 'label_interest_register_field_description',
                },
                {
                    key: 'conflictingRelations',
                    labelKey: 'label_interest_register_field_conflicting_relations',
                },
                {
                    key: 'measuresTaken',
                    labelKey: 'label_interest_register_field_measures_taken',
                },
            ],
        },
    ],

    [InterestRegisterEntryType.GiftOrTravel]: [
        {
            titleKey: 'label_interest_register_section_gift_or_travel',
            fields: [
                {
                    key: 'type',
                    fallbackKeys: ['giftOrTravelType'],
                    labelKey: 'label_interest_register_field_gift_or_travel_type',
                    valueMap: {
                        Gift: 'Gjöf',
                        gift: 'Gjöf',
                        Gjöf: 'Gjöf',
                        Invitation: 'Boð',
                        invitation: 'Boð',
                        Boð: 'Boð',
                        Travel: 'Boðsferð',
                        travel: 'Boðsferð',
                        Boðsferð: 'Boðsferð',
                    },
                },
                {
                    key: 'offeredBy',
                    fallbackKeys: ['giver', 'provider'],
                    labelKey: 'label_interest_register_field_offered_by',
                },
                {
                    key: 'description',
                    fallbackKeys: ['giftOrTravelDescription'],
                    labelKey: 'label_interest_register_field_gift_or_travel_description',
                },
                {
                    key: 'date',
                    labelKey: 'label_interest_register_field_date',
                    type: 'date',
                },
                {
                    key: 'estimatedValue',
                    fallbackKeys: ['value'],
                    labelKey: 'label_interest_register_field_estimated_value',
                    type: 'currency',
                },
            ],
        },
    ],

    [InterestRegisterEntryType.EmploymentOrBoardParticipation]: [
        {
            titleKey: 'label_interest_register_section_employment_or_board',
            fields: [
                {
                    key: 'participationType',
                    labelKey: 'label_interest_register_field_participation_type',
                    valueMap: {
                        Employment: 'Launað starf',
                        employment: 'Launað starf',
                        'Launað starf': 'Launað starf',
                        Atvinnuþátttaka: 'Launað starf',

                        EmploymentActivity: 'Atvinnustarfsemi',
                        employmentActivity: 'Atvinnustarfsemi',
                        Atvinnustarfsemi: 'Atvinnustarfsemi',

                        BoardParticipation: 'Stjórnarseta',
                        boardParticipation: 'Stjórnarseta',
                        Stjórnarseta: 'Stjórnarseta',

                        InvestmentCouncil: 'Seta í fjárfestingarráði',
                        investmentCouncil: 'Seta í fjárfestingarráði',
                        'Seta í fjárfestingarráði': 'Seta í fjárfestingarráði',

                        InterestCouncil: 'Seta í hagsmunaráði',
                        interestCouncil: 'Seta í hagsmunaráði',
                        'Seta í hagsmunaráði': 'Seta í hagsmunaráði',
                    },
                },
                {
                    key: 'memberRole',
                    fallbackKeys: ['roleType', 'boardRoleType', 'memberType'],
                    labelKey: 'label_interest_register_field_role_type',
                    valueMap: {
                        Primary: 'Aðalmaður',
                        primary: 'Aðalmaður',
                        Main: 'Aðalmaður',
                        main: 'Aðalmaður',
                        Alternate: 'Varamaður',
                        alternate: 'Varamaður',
                        Substitute: 'Varamaður',
                        substitute: 'Varamaður',
                        Aðalmaður: 'Aðalmaður',
                        Varamaður: 'Varamaður',
                    },
                },
                {
                    key: 'startOrEnd',
                    labelKey: 'label_interest_register_field_start_or_end',
                    valueMap: {
                        Start: 'Upphaf',
                        start: 'Upphaf',
                        End: 'Lok',
                        end: 'Lok',
                        Upphaf: 'Upphaf',
                        Lok: 'Lok',
                    },
                },
                {
                    key: 'date',
                    fallbackKeys: ['startDate', 'endDate', 'fromDate', 'toDate'],
                    labelKey: 'label_interest_register_field_date',
                    type: 'date',
                },
                {
                    key: 'isRelatedToLsrRole',
                    fallbackKeys: ['relatedToLsrWork', 'isRelatedToLsrWork'],
                    labelKey: 'label_interest_register_field_related_to_lsr_work',
                    type: 'boolean',
                },
                {
                    key: 'compensation',
                    fallbackKeys: ['fee', 'payment'],
                    labelKey: 'label_interest_register_field_compensation',
                    type: 'currency',
                },
                {
                    key: 'companyName',
                    labelKey: 'label_interest_register_field_company_name',
                },
                {
                    key: 'companyType',
                    labelKey: 'label_interest_register_field_company_type',
                },
                {
                    key: 'otherRelations',
                    labelKey: 'label_interest_register_field_other_relations',
                },
                {
                    key: 'otherDescription',
                    fallbackKeys: ['other'],
                    labelKey: 'label_interest_register_field_other',
                },
            ],
        },
    ],

    [InterestRegisterEntryType.InterDepartmentTrading]: [
        {
            titleKey: 'label_interest_register_section_department_trading',
            fields: [
                {
                    key: 'transactionDate',
                    fallbackKeys: ['tradeDate', 'date'],
                    labelKey: 'label_interest_register_field_trade_date',
                    type: 'date',
                },
                {
                    key: 'buyer',
                    labelKey: 'label_interest_register_field_buyer',
                },
                {
                    key: 'seller',
                    labelKey: 'label_interest_register_field_seller',
                },
                {
                    key: 'financialInstrumentName',
                    fallbackKeys: ['instrumentName', 'instrument', 'financialInstrument', 'instrumentIdentifier'],
                    labelKey: 'label_interest_register_field_instrument_name',
                },
                {
                    key: 'nominalValue',
                    fallbackKeys: ['nominalAmount'],
                    labelKey: 'label_interest_register_field_nominal_value',
                    type: 'number',
                },
                {
                    key: 'yieldOrPrice',
                    fallbackKeys: ['priceYield', 'rateOrPrice'],
                    labelKey: 'label_interest_register_field_yield_or_price',
                    type: 'decimal',
                },
                {
                    key: 'transactionTermsReference',
                    labelKey: 'label_interest_register_field_transaction_terms_reference',
                },
                {
                    key: 'interestAssessment',
                    labelKey: 'label_interest_register_field_interest_assessment',
                },
            ],
        },
    ],

    [InterestRegisterEntryType.MyEntries]: [],
    [InterestRegisterEntryType.AllEntries]: [],
};

export const INTEREST_REGISTER_INFO_TEXTS: Partial<Record<InterestRegisterEntryType, InterestRegisterInfoTextConfig>> = {
    [InterestRegisterEntryType.ConflictOfInterest]: {
        summaryTextKey: 'text_interest_register_info_conflict_summary',
        detailTextKeys: ['text_interest_register_info_conflict_detail_1', 'text_interest_register_info_conflict_detail_2'],
    },
    [InterestRegisterEntryType.SecuritiesTrading]: {
        summaryTextKey: 'text_interest_register_info_securities_summary',
        detailTextKeys: [
            'text_interest_register_info_securities_detail_1',
            'text_interest_register_info_securities_detail_2',
            'text_interest_register_info_securities_detail_3',
            'text_interest_register_info_securities_detail_4',
        ],
        linkIntroTextKey: 'text_interest_register_info_securities_link_intro',
        linkTextKey: 'text_interest_register_info_securities_link_text',
        linkUrl: 'https://lsr.coredata.is/doc/864c24fa-6a1b-42ce-ae06-89ac43874ba4/',
    },
    [InterestRegisterEntryType.GiftOrTravel]: {
        summaryTextKey: 'text_interest_register_info_gift_travel_summary',
        detailTextKeys: [
            'text_interest_register_info_gift_travel_travel_1',
            'text_interest_register_info_gift_travel_gift_invitation_1',
            'text_interest_register_info_gift_travel_gift_invitation_2',
            'text_interest_register_info_gift_travel_gift_invitation_3',
            'text_interest_register_info_gift_travel_gift_invitation_4',
        ],
    },
    [InterestRegisterEntryType.EmploymentOrBoardParticipation]: {
        summaryTextKey: 'text_interest_register_info_employment_board_summary',
        detailTextKeys: [
            'text_interest_register_info_employment_board_detail_1',
            'text_interest_register_info_employment_board_detail_2',
            'text_interest_register_info_employment_board_detail_3',
        ],
    },
    [InterestRegisterEntryType.InterDepartmentTrading]: {
        summaryTextKey: 'text_interest_register_info_department_trading_summary',
        detailTextKeys: [
            'text_interest_register_info_department_trading_detail_1',
            'text_interest_register_info_department_trading_detail_2',
            'text_interest_register_info_department_trading_detail_3',
            'text_interest_register_info_department_trading_link_note',
        ],
    },
};
